import { useCallback, useMemo, useState } from 'react';
import { EventSearch } from 'src/components/Events/EventSearch';
import {
  EventSearchResult,
  SearchEventsQuery,
} from 'src/components/Events/EventSearch/EventSearch.types';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { DateRangeSelector } from 'src/core/POS/DateRangeSelector';
import { PosFormField } from 'src/core/POS/PosFormField';
import { getTextFieldState, PosTextField } from 'src/core/POS/PosTextField';
import { Button, Stack } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { NextDateRangePresetNames } from 'src/utils/dateTimeUtils';
import { DateTimeRange, EventGroupClient } from 'src/WebApiController';

import { SectionContent } from '../common';

// ─── Component ────────────────────────────────────────────────────────────────

export type CreateSeasonModalProps = {
  /** Invoked after the season is created, with the new event group's id. */
  onSuccess: (eventGroupId: string) => void;
  onClose: () => void;
};

export function CreateSeasonModal({
  onSuccess,
  onClose,
}: CreateSeasonModalProps) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();

  const [seasonName, setSeasonName] = useState('');
  const [dateRange, setDateRange] = useState<DateTimeRange | null>(null);
  const [selectedEvents, setSelectedEvents] = useState<EventSearchResult[]>([]);
  // Number of events the user typed. The effective total is always at least the
  // number of selected events (see totalEvents below).
  const [numberOfEvents, setNumberOfEvents] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);

  const seasonNamePlaceholder = useContent(ContentId.SeasonNamePlaceholder);
  const seasonNameRequiredMsg = useContent(ContentId.SeasonNameRequired);
  const dateRangeRequiredMsg = useContent(ContentId.DateRangeRequired);
  const selectAtLeastOneEventMsg = useContent(
    ContentId.SelectAtLeastOneEventOrNumber
  );

  const selectedVirtIds = selectedEvents.map((e) => e.event.virtId);

  // The effective total is the greater of the typed number and the events
  // selected below — you can't have fewer events than you've selected.
  const totalEvents = Math.max(numberOfEvents, selectedEvents.length);

  const eventSearchQuery = useMemo<SearchEventsQuery>(
    () => ({
      venueId: null,
      venue: null,
      performerId: null,
      performer: null,
      searchText: null,
      timeFrame: null,
    }),
    []
  );
  const eventSearchKey = `${dateRange?.start ?? ''}|${dateRange?.end ?? ''}`;

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleEventsSelect = useCallback((results: EventSearchResult[]) => {
    setSelectedEvents((prev) => {
      // Merge new selections, avoiding duplicates by virtId
      const existingIds = new Set(prev.map((e) => e.event.virtId));
      const added = results.filter(
        (r) => r.isSelected && !existingIds.has(r.event.virtId)
      );
      const removed = new Set(
        results.filter((r) => !r.isSelected).map((r) => r.event.virtId)
      );
      const kept = prev.filter((e) => !removed.has(e.event.virtId));
      return [...kept, ...added];
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (isSaving) return;

    if (!seasonName.trim()) {
      setNameError(seasonNameRequiredMsg);
      return;
    }
    if (!dateRange?.start || !dateRange?.end) {
      setDateRangeError(dateRangeRequiredMsg);
      return;
    }
    if (totalEvents < 1) {
      setNameError(selectAtLeastOneEventMsg);
      return;
    }
    setNameError(null);
    setDateRangeError(null);
    setIsSaving(true);

    try {
      const eventGroupId = window.crypto.randomUUID();

      await new EventGroupClient(activeAccountWebClientConfig).createEventGroup(
        {
          eventGroupId,
          name: seasonName.trim(),
          selectedEvents: selectedEvents.map(({ event }) => ({
            viagogoEventId: event.viagId,
            eventMappingId: event.viagId == null ? event.virtId : null,
            eventDateTime: event.dates?.start ?? null,
          })),
          startDate: dateRange.start,
          endDate: dateRange.end,
        }
      );

      onSuccess(eventGroupId);
    } catch (err: unknown) {
      trackError('CreateSeasonModal.handleSave', err as Error, {
        seasonName,
        selectedCount: selectedEvents.length,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    activeAccountWebClientConfig,
    dateRange,
    dateRangeRequiredMsg,
    isSaving,
    onSuccess,
    seasonName,
    seasonNameRequiredMsg,
    selectAtLeastOneEventMsg,
    selectedEvents,
    totalEvents,
    trackError,
  ]);

  const canSave = useMemo(() => {
    return (
      !!seasonName &&
      !!dateRange &&
      numberOfEvents > 0 &&
      numberOfEvents === selectedEvents.length
    );
  }, [dateRange, numberOfEvents, seasonName, selectedEvents.length]);

  const requestCount = useMemo(() => {
    return Math.max(0, totalEvents - selectedEvents.length);
  }, [selectedEvents.length, totalEvents]);

  // ── Render ──────────────────────────────────────────────────────────────────

  const footer = (
    <Stack direction="row" gap="m" justifyContent="end">
      <Button variant="outline" onClick={onClose} disabled={isSaving}>
        <Content id={ContentId.Cancel} />
      </Button>
      <Button
        variant="regular"
        onClick={handleSave}
        disabled={!canSave || isSaving}
      >
        <Content id={isSaving ? ContentId.Saving : ContentId.Save} />
      </Button>
    </Stack>
  );

  return (
    <GenericDialog
      isOpen
      header={<Content id={ContentId.CreateSeason} />}
      footer={footer}
      onCancel={onClose}
      size="lg"
    >
      <Stack direction="column" gap="s" style={{ minHeight: '400px' }}>
        {/* Info fields — one per row; drive the EventSearch filter below */}
        <SectionContent numOfColumns={1} sameWidth>
          <PosFormField
            label={<Content id={ContentId.Name} />}
            errors={nameError ?? undefined}
          >
            <PosTextField
              id="season-name-input"
              type="text"
              placeholder={seasonNamePlaceholder}
              value={seasonName}
              errorState={getTextFieldState(nameError ?? undefined)}
              onChange={(e) => {
                setSeasonName(e.target.value);
                if (nameError) setNameError(null);
              }}
            />
          </PosFormField>
        </SectionContent>
        <SectionContent numOfColumns={2} sameWidth>
          <PosFormField
            label={<Content id={ContentId.DateRange} />}
            errors={dateRangeError ?? undefined}
          >
            <DateRangeSelector
              value={dateRange}
              onChange={({ value }) => {
                setDateRange((value as DateTimeRange | null) ?? null);
                if (dateRangeError) setDateRangeError(null);
              }}
              presetNames={NextDateRangePresetNames}
            />
          </PosFormField>
          <PosFormField label={<Content id={ContentId.NumberOfEvents} />}>
            <PosTextField
              type="number"
              min={0}
              // Reflects max(typed, selected) — you can't request fewer than
              // you've selected below.
              value={String(totalEvents)}
              onChange={(e) =>
                setNumberOfEvents(Math.max(0, Number(e.target.value) || 0))
              }
            />
          </PosFormField>
        </SectionContent>

        {/* Event search — filtered by the info fields above.
            EventSearch fills its parent's height (height: 100%) and its
            results list is virtualized, so it needs a bounded-height
            container — GenericDialog's body is content-sized, so give the
            list an explicit height here or nothing renders. */}
        <Stack direction="column" gap="s">
          <span style={{ fontWeight: 600, fontSize: '14px' }}>
            <Content id={ContentId.SelectEvents} />
          </span>
          <div style={{ height: 460 }}>
            <EventSearch
              key={eventSearchKey}
              selectedEvents={selectedVirtIds}
              numEventsSelected={selectedEvents.length}
              onEventsSelect={handleEventsSelect}
              onDeleteNumEventsSelected={() => setSelectedEvents([])}
              defaultDateFilter={dateRange ?? undefined}
              defaultSearchEventsQuery={eventSearchQuery}
              allowSearchByVenueConfig
              requestCount={requestCount}
            />
          </div>
        </Stack>
      </Stack>
    </GenericDialog>
  );
}
